import type { QuizReviewQuestion } from "@/client"
import { useColorModeValue } from "@/components/ui/color-mode"
import { Badge, Box, Button, Card, Stack, Text } from "@chakra-ui/react"

export type ReviewQuestionFilter = "all" | "correct" | "incorrect"

interface ReviewQuestionSummaryProps {
  questions: QuizReviewQuestion[]
  selectedFilter: ReviewQuestionFilter
  onFilterChange: (filter: ReviewQuestionFilter) => void
}

function isCorrectQuestion(question: QuizReviewQuestion) {
  return question.user_answer !== null && question.is_correct
}

export function ReviewQuestionSummary({
  questions,
  selectedFilter,
  onFilterChange,
}: ReviewQuestionSummaryProps) {
  const correctCount = questions.filter(isCorrectQuestion).length
  const incorrectCount = questions.length - correctCount
  const filters: Array<{
    filter: ReviewQuestionFilter
    label: string
    count: number
    colorPalette: "gray" | "green" | "red"
  }> = [
    {
      filter: "all",
      label: "All",
      count: questions.length,
      colorPalette: "gray",
    },
    {
      filter: "correct",
      label: "Correct",
      count: correctCount,
      colorPalette: "green",
    },
    {
      filter: "incorrect",
      label: "Incorrect",
      count: incorrectCount,
      colorPalette: "red",
    },
  ]

  const borderColor = useColorModeValue("gray.200", "gray.700")
  const mutedTextColor = useColorModeValue("gray.600", "gray.300")

  return (
    <Card.Root p={4} borderColor={borderColor}>
      <Stack gap={4}>
        <Box>
          <Text fontWeight="semibold">Review summary</Text>
          <Text fontSize="sm" color={mutedTextColor}>
            Use the dots to jump to a question or filter your review.
          </Text>
        </Box>

        <Stack gap={2}>
          {filters.map(({ filter, label, count, colorPalette }) => (
            <Button
              key={filter}
              variant={selectedFilter === filter ? "solid" : "outline"}
              colorPalette={colorPalette}
              justifyContent="space-between"
              onClick={() => onFilterChange(filter)}
              aria-pressed={selectedFilter === filter}
            >
              <span>{label}</span>
              <Badge colorPalette={colorPalette}>{count}</Badge>
            </Button>
          ))}
        </Stack>
      </Stack>
    </Card.Root>
  )
}
